import { useMutation } from "@tanstack/react-query";
import { BenefitLayout } from "providers/benefit-layout";
import React, { FC, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { DateInput } from "shared/components/forms/DateInput";
import { Button, ButtonVariant } from "shared/components/Button";
import { Input } from "shared/components/forms/Input";
import { useForm } from "shared/hooks/use-form";
import api from "api";
import { SuccessScreenView } from "../../../../components/SuccessScreen";
import { leaseDetailsValidations } from "../../../../validations";
import { View } from "views/rent-reporting-id-issues-m2m-lease-end/types";
import { Money } from "types/money";

type Props = {
  leaseEndDate: string;
  rentAmount: Money;
  setView: (view: View) => void;
};

enum Steps {
  UpdateDetails = "UpdateDetails",
  Success = "Success"
}

export const NewLeaseEndDateView: FC<Props> = ({
  leaseEndDate,
  rentAmount,
  setView
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { register, onSubmit, handleChange } = useForm(
    {
      leaseEndDate: "",
      rentAmount: ""
    },
    leaseDetailsValidations
  );
  const [step, setStep] = useState<Steps>(Steps.UpdateDetails);

  const { mutate: renewLeaseDetails } = useMutation({
    mutationKey: ["UPDATE_LEASE_DETAILS"],
    mutationFn: ({
      lease_end_date,
      monthly_rent_amount
    }: {
      lease_end_date: string;
      monthly_rent_amount: {
        cents: number;
        currency: "USD";
      };
    }) =>
      api.renewLeaseOptions({
        id: Number(id),
        renew_lease: true,
        pending_lease_end_date: lease_end_date,
        pending_monthly_rent_amount: monthly_rent_amount
      }),
    onError: (error) => {
      toast.error(error.message || "An error occurred");
    },
    onSuccess: () => {
      setStep(Steps.Success);
    }
  });

  useEffect(() => {
    handleChange("rentAmount", () => (rentAmount.cents || 0) / 100)({});
  }, [rentAmount]);

  if (step === Steps.UpdateDetails)
    return (
      <BenefitLayout
        hasBack={true}
        title="Please update your lease details"
        mainClassName="max-w-[440px] w-full mx-auto md:mt-4"
        footer={
          <div className="max-w-[440px] mx-auto w-full">
            <Button
              variant={ButtonVariant.PRIMARY}
              className="w-full"
              type="submit"
              form="renew-details-form"
            >
              Update
            </Button>

            <Button
              className="w-full md:block hidden mt-2"
              onClick={() => setView(View.SelectView)}
            >
              Back
            </Button>
          </div>
        }
      >
        <form
          id="renew-details-form"
          onSubmit={onSubmit({
            onSuccess: (data) => {
              renewLeaseDetails({
                lease_end_date: data.leaseEndDate,
                monthly_rent_amount: {
                  cents: data.rentAmount * 100,
                  currency: "USD"
                }
              });
            },
            onError: (e) => {
              toast.error(Object.values(e).join(", "));
            }
          })}
        >
          <DateInput
            id="lease_end_date"
            label="Lease end"
            hideOptional
            {...register("leaseEndDate")}
          />
          <Input
            id="rentAmount"
            label="Rent amount"
            placeholder="Rent amount"
            {...register("rentAmount")}
            type="money"
            hideOptional
          />
        </form>
      </BenefitLayout>
    );

  return (
    <SuccessScreenView
      title="Great!"
      subtext1={`On ${leaseEndDate} you will see these changes reflected in your app.`}
      buttonTitle="Done"
      onButtonClick={() => navigate("/hub")}
    />
  );
};
